import { Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { first } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import {
  ValidatedInputRepeaterPercentSumRow,
  validateExactly100Percent,
} from '@/components/form/formAwareInputs/FormAwareInputRepeater/ValidatedInputRepeaterPercentSumRow';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';

import { useIsCRT } from '../CharitableTrustDesignerContext';
import {
  CharitableTrustDesignerBasicInformationBeneficiaryOption,
  CharitableTrustDesignerBasicInformationForm,
  CharitableTrustDesignerBasicInformationFormPaths,
  NAMESPACE,
} from './CharitableTrustDesignerBasicInformation.types';

interface BeneficiaryCardProps {
  options: CharitableTrustDesignerBasicInformationBeneficiaryOption[];
  onClickAddNew?: () => void;
}

interface BeneficiaryRepeaterCardProps extends BeneficiaryCardProps {
  fieldName: 'charitableBeneficiaries' | 'nonCharitableBeneficiaries';
}

function BeneficiaryRepeaterCard({
  fieldName,
  options,
  onClickAddNew,
}: BeneficiaryRepeaterCardProps) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  const { control, getValues, setValue } =
    useFormContext<CharitableTrustDesignerBasicInformationForm>();

  const owners = useWatch({
    name: `${NAMESPACE}.${fieldName}`,
    control,
  });

  const ownershipSum = owners.reduce<Decimal>(
    (acc, { percentageOwnership }) => {
      acc = acc.add(percentageOwnership || new Decimal(0));
      return acc;
    },
    new Decimal(0)
  );

  const validationField =
    `${NAMESPACE}._validationFields.${fieldName}` as const satisfies CharitableTrustDesignerBasicInformationFormPaths;

  useEffect(() => {
    setValue(validationField, ownershipSum);
  }, [setValue, ownershipSum, validationField]);

  return (
    <FormAwareInputRepeater<CharitableTrustDesignerBasicInformationForm>
      control={control}
      name={
        `${NAMESPACE}.${fieldName}` as const satisfies CharitableTrustDesignerBasicInformationFormPaths
      }
      emptyValue={{
        id: '',
        percentageOwnership: null,
      }}
      addAnotherItemText="Add beneficiary"
      rightFooterContent={
        <ValidatedInputRepeaterPercentSumRow<CharitableTrustDesignerBasicInformationForm>
          decimalScale={2}
          validationField={validationField}
          validation={{
            totalValue: useCallback(
              (value) => {
                // beneficiaries are optional, so only validate if there's an owner with an ID present
                const hasValidOwner = owners.find((owner) => !!owner.id);
                if (!hasValidOwner) {
                  return undefined;
                }
                return validateExactly100Percent(value as Decimal | null);
              },
              [owners]
            ),
          }}
        />
      }
      render={(index) => {
        const baseFieldName = `${NAMESPACE}.${fieldName}.${index}` as const;
        const value = getValues(`${baseFieldName}.id`);
        return (
          <FormLayoutRow>
            <FormLayoutItem width={8}>
              <TypeaheadSelectInput<string>
                value={value}
                disabled={disabledFromContext}
                onChange={(_event, targetId) => {
                  if (!targetId) {
                    setValue(
                      `${baseFieldName}.id` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      ''
                    );
                    setValue(
                      `${baseFieldName}.kind` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      null
                    );
                    setValue(
                      `${baseFieldName}.beneficiaryNodeId` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      null
                    );
                    setValue(
                      `${baseFieldName}.percentageOwnership` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      null
                    );
                  } else {
                    const matchingOption = options.find(
                      (option) => option.value === targetId
                    );
                    if (!matchingOption) {
                      return;
                    }

                    setValue(
                      `${baseFieldName}.id` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      matchingOption.value
                    );
                    setValue(
                      `${baseFieldName}.kind` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      matchingOption.kind
                    );
                    setValue(
                      `${baseFieldName}.beneficiaryNodeId` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                      null
                    );

                    // when setting the first owner, default the percentage ownership to 100%
                    if (
                      owners.length === 1 &&
                      first(owners)?.percentageOwnership === null
                    ) {
                      setValue(
                        `${baseFieldName}.percentageOwnership` as const satisfies CharitableTrustDesignerBasicInformationFormPaths,
                        new Decimal(100)
                      );
                    }
                  }
                }}
                label="Beneficiary"
                options={options}
                addNewOption={
                  onClickAddNew ? { onClick: onClickAddNew } : undefined
                }
                placeholder="Search beneficiaries"
                groupBy={(option) => option.groupName ?? ''}
                hideLabel={index >= 1}
              />
            </FormLayoutItem>
            <FormLayoutItem width={4}>
              <FormAwarePercentInput
                control={control}
                fieldName={
                  `${baseFieldName}.percentageOwnership` as const satisfies CharitableTrustDesignerBasicInformationFormPaths
                }
                label="Percentage"
                hideLabel={index >= 1}
                isDecimalJSInput
                fixedDecimalScale
                decimalScale={2}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        );
      }}
    />
  );
}

export function CharitableBeneficiariesCard({
  options,
  onClickAddNew,
}: BeneficiaryCardProps) {
  const isCRT = useIsCRT();

  return (
    <Card variant="outlined" sx={{ p: 2, pb: 4 }}>
      <Stack direction="column">
        <Typography variant="title-s" sx={{ pb: 0.5 }}>
          Charitable {isCRT ? 'remainder' : 'income'} beneficiaries
        </Typography>
        <Typography variant="body-xs" sx={{ pb: 2 }}>
          {isCRT ? (
            <>
              Select the charitable beneficiaries that will receive the
              trust&apos;s remainder at the end of the trust&apos;s term.
            </>
          ) : (
            <>
              Select the charitable beneficiaries that will receive the income
              payments from the trust.
            </>
          )}
        </Typography>
        <BeneficiaryRepeaterCard
          onClickAddNew={onClickAddNew}
          options={options}
          fieldName={'charitableBeneficiaries'}
        />
      </Stack>
    </Card>
  );
}

export function NonCharitableBeneficiariesCard({
  options,
  onClickAddNew,
}: BeneficiaryCardProps) {
  const isCRT = useIsCRT();
  return (
    <Card variant="outlined" sx={{ p: 2, pb: 4 }}>
      <Stack direction="column">
        <Typography variant="title-s" sx={{ pb: 0.5 }}>
          {isCRT ? 'Income' : 'Remainder'} beneficiaries
        </Typography>
        <Typography variant="body-xs" sx={{ pb: 2 }}>
          {isCRT ? (
            <>
              Select the beneficiaries that will receive the income payments
              from the trust.
            </>
          ) : (
            <>
              Add remainder beneficiaries that will receive the trust&apos;s
              remainder at the end of the trust&apos;s term. Remainder
              beneficiaries are often trusts for the benefit of family members.
            </>
          )}
        </Typography>
        <BeneficiaryRepeaterCard
          onClickAddNew={onClickAddNew}
          options={options}
          fieldName={'nonCharitableBeneficiaries'}
        />
      </Stack>
    </Card>
  );
}
